'use client';

import type { JSX } from 'react';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  AriaGridListProps,
  DragPreviewRenderer,
  DroppableCollectionResult,
} from 'react-aria';
import type {
  DraggableCollectionState,
  DroppableCollectionState,
  Key,
  ListProps,
  Node,
} from 'react-stately';
import {
  AnimateItemGroup,
  AnimateMutationProvider,
} from 'components/Collections/animations';
import { DragAndDropProvider } from 'components/Collections/dnd/DragAndDropProvider';
import type { DragAndDropHooks } from 'components/Collections/dnd/useDragAndDrop';
import { useSize } from 'components/Collections/hooks/useSize';
import type { LayoutInfo } from 'components/Collections/layouts/types';
import { useFilteredListState } from 'components/Collections/List';
import { Portal } from 'components/Portal';
import { useReducedMotion } from 'hooks';
import styles from './Carousel.module.css';
import { CarouselArrowButton } from './CarouselArrow';
import { CarouselListItem } from './CarouselListItem';
import type { CarouselVariant } from './types';
import { useCarouselList } from './useCarouselList';

export type CarouselProps<T> = Pick<ListProps<T>, 'children' | 'items'> & {
  /** The activated key */
  activatedId?: Key;
  /**
   * The estimated size of the container that is used on first render, and before
   * the container element has been measured. Used for SSR. Use -1 to disable
   * the default size and wait for measurements until first render.
   */
  estimatedContainerSize?: number;
  /** The gap between each card */
  itemGap?: number;
  /** The height of each card */
  itemHeight?: number;
  /** The width of each card */
  itemWidth?: number;
  /** The padding provided at the ends of the scroll container */
  paddingEnd?: number;
  /** Basic orientation of the carousel */
  orientation?: 'horizontal';
  /** Padding that appears above the cards */
  paddingTop?: number;
  /** Ensure that enough bottom padding is provided for rendering the card labels */
  paddingBottom?: number;
  /** Animate item mutations (additions, removals) */
  animate?: boolean;
  /** Attach drag and drop handler hooks provided by `useDragAndDrop`. */
  dragAndDropHooks?: DragAndDropHooks;
  variant?: CarouselVariant;
} & AriaGridListProps<T>;

export function Carousel<T extends object>({
  children,
  items,
  itemWidth = 100,
  itemHeight = 60,
  itemGap = 24,
  estimatedContainerSize = 1000,
  paddingEnd = 16,
  paddingTop = 24,
  paddingBottom = 56,
  activatedId,
  orientation = 'horizontal',
  animate: animateMutationsProp = true,
  variant = 'small',
  ...other
}: CarouselProps<T>) {
  const ref = useRef<HTMLDivElement>(undefined);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [animatePage, setAnimate] = useState(false);

  const isReducedMotion = useReducedMotion();

  const animateMutations = isReducedMotion ? false : animateMutationsProp;
  const animate = isReducedMotion ? false : animatePage;

  const next = () => {
    setAnimate(true);
    setActiveGroupIndex((prev) => prev + 1);
  };

  const prev = () => {
    setAnimate(true);
    setActiveGroupIndex((prev) => prev - 1);
  };

  const [bounds, setBounds] = useState({
    width: estimatedContainerSize,
    height: estimatedContainerSize,
  });

  // @ts-expect-error FIXME(strictNullChecks)
  useSize(ref, (size) => {
    setAnimate(false);
    setBounds(size);
  });

  const size = bounds.width;
  const itemSize = itemWidth;

  const { state } = useFilteredListState({
    children,
    items,
    variant,
    ...other,
  });

  const itemsPerGroup = Math.floor(
    (size - paddingEnd * 2 + itemGap) / (itemSize + itemGap)
  );

  // calculate the layout
  const { layoutInfo, nodes } = useMemo(() => {
    const layoutInfo = new Map<
      Key,
      LayoutInfo & {
        index: number;
        groupIndex: number;
      }
    >();

    const groupedItems = [...state.collection].reduce((acc, item, index) => {
      const groupIndex = Math.floor(index / itemsPerGroup);
      acc[groupIndex] = acc[groupIndex] || [];
      acc[groupIndex].push(item);

      // Calculate total width of items in group including gaps
      const groupWidth =
        itemWidth * itemsPerGroup + itemGap * (itemsPerGroup - 1);
      // Calculate left offset to center the group
      const groupStartX = (size - groupWidth) / 2;
      // Calculate item position within group
      const x =
        groupStartX +
        (index % itemsPerGroup) * (itemWidth + itemGap) +
        groupIndex * size;

      layoutInfo.set(item.key, {
        type: 'item',
        key: item.key,
        parentKey: null,
        index,
        groupIndex,
        rect: {
          x,
          y: paddingTop,
          width: itemWidth,
          height: itemHeight,
        },
        isMeasured: true,
      });

      return acc;
    }, [] as Array<Array<Node<T>>>);

    return {
      layoutInfo,
      nodes: groupedItems,
    };
  }, [
    itemGap,
    itemHeight,
    itemWidth,
    itemsPerGroup,
    paddingTop,
    size,
    state.collection,
  ]);

  const currentIndex = Math.max(
    0,
    Math.min(activeGroupIndex, nodes.length - 1)
  );

  const { carouselListProps, keyboardDelegate, dropTargetDelegate } =
    // @ts-expect-error FIXME(strictNullChecks)
    useCarouselList(ref, state, {
      ...other,
      children,
      items,
    });

  // Keep track of the last focused key to determine if we should animate the carousel
  // when the focused key changes.
  const lastFocusedKey = useRef<Key>(null);

  useLayoutEffect(() => {
    if (lastFocusedKey.current !== state.selectionManager.focusedKey) {
      if (state.selectionManager.focusedKey) {
        // @ts-expect-error FIXME(strictNullChecks)
        const groupIndex = layoutInfo.get(
          state.selectionManager.focusedKey
        ).groupIndex;
        if (groupIndex !== undefined) {
          setActiveGroupIndex(groupIndex);
          setAnimate(true);
        }
      }
    }
    lastFocusedKey.current = state.selectionManager.focusedKey;
  }, [state.selectionManager.focusedKey, layoutInfo]);

  const visibleRect = useMemo(() => {
    return {
      x: 0,
      y: paddingTop,
      width: size * nodes.length,
      height: itemHeight + paddingBottom,
    };
  }, [nodes, itemHeight, size, paddingTop, paddingBottom]);

  /**
   * Draggable support
   */

  const { dragAndDropHooks } = other;

  let dragState: DraggableCollectionState;
  let dragPreview: JSX.Element | null = null;
  const preview = useRef<DragPreviewRenderer>(null);

  const isListDraggable = !!dragAndDropHooks?.useDraggableCollectionState;

  if (isListDraggable && dragAndDropHooks) {
    dragState = dragAndDropHooks.useDraggableCollectionState!({
      collection: state.collection,
      selectionManager: state.selectionManager,
      preview: dragAndDropHooks.renderDragPreview ? preview : undefined,
    });

    // @ts-expect-error FIXME(strictNullChecks)
    dragAndDropHooks.useDraggableCollection!({}, dragState, ref);

    const DragPreview = dragAndDropHooks.DragPreview!;

    dragPreview = dragAndDropHooks.renderDragPreview ? (
      <Portal>
        <DragPreview ref={preview}>
          {dragAndDropHooks.renderDragPreview}
        </DragPreview>
      </Portal>
    ) : null;
  }

  /**
   * Droppable support
   */

  let dropState: DroppableCollectionState;
  let droppableCollection: DroppableCollectionResult;
  let isRootDropTarget = false;

  const isListDroppable = !!dragAndDropHooks?.useDroppableCollectionState;

  if (isListDroppable && dragAndDropHooks) {
    dropState = dragAndDropHooks.useDroppableCollectionState!({
      collection: state.collection,
      selectionManager: state.selectionManager,
    });

    droppableCollection = dragAndDropHooks.useDroppableCollection!(
      {
        keyboardDelegate,
        dropTargetDelegate,
      },
      dropState,
      // @ts-expect-error FIXME(strictNullChecks)
      ref
    );

    isRootDropTarget = dropState.isDropTarget({ type: 'root' });
  }

  return (
    <DragAndDropProvider
      dragAndDropHooks={dragAndDropHooks}
      // @ts-expect-error FIXME(strictNullChecks)
      dragState={dragState}
      // @ts-expect-error FIXME(strictNullChecks)
      dropState={dropState}
    >
      <AnimateMutationProvider
        duration={350}
        enabled={animateMutations}
        isFullyMeasured={true}
        layoutInfo={layoutInfo}
        state={state}
        visibleRect={visibleRect}
      >
        <div
          className={styles.container}
          data-orientation={orientation}
          data-root-drop-target={isRootDropTarget || undefined}
          style={
            {
              '--item-width': `${itemWidth}px`,
              '--item-height': `${itemHeight}px`,
              '--item-gap': `${itemGap}px`,
              '--padding-top': `${paddingTop}px`,
              '--padding-bottom': `${paddingBottom}px`,
            } as React.CSSProperties
          }
        >
          <CarouselArrowButton
            isDisabled={currentIndex === 0}
            label="Previous"
            onPress={prev}
            orientation={orientation}
            placement="start"
          />
          <div className={styles.mask}>
            <div
              // @ts-expect-error FIXME(strictNullChecks)
              ref={ref}
              // @ts-expect-error FIXME(strictNullChecks)
              aria-label="Carousel"
              className={styles.scrollContainer}
              style={{
                transition: animate ? 'transform 0.3s ease' : 'none',
                transform: `translateX(-${currentIndex * bounds.width}px)`,
              }}
              // @ts-expect-error FIXME(strictNullChecks)
              {...droppableCollection?.collectionProps}
              {...carouselListProps}
            >
              <AnimateItemGroup>
                {nodes.map((group, i) => {
                  const shouldRender =
                    i === currentIndex ||
                    i === currentIndex + 1 ||
                    i === currentIndex - 1;

                  return group.map((item, itemIndex) => {
                    const info = layoutInfo.get(item.key);
                    return (
                      <CarouselListItem
                        key={item.key}
                        height={itemHeight}
                        index={itemIndex + i * itemsPerGroup}
                        isActivated={item.key === activatedId}
                        item={item}
                        itemGap={itemGap}
                        // @ts-expect-error FIXME(strictNullChecks)
                        onAction={other.onAction}
                        shouldRender={shouldRender}
                        state={state}
                        totalCount={state.collection.size}
                        width={itemWidth}
                        // @ts-expect-error FIXME(strictNullChecks)
                        x={info.rect.x}
                        // @ts-expect-error FIXME(strictNullChecks)
                        y={info.rect.y}
                      />
                    );
                  });
                })}
              </AnimateItemGroup>
            </div>
          </div>

          <CarouselArrowButton
            isDisabled={currentIndex === nodes.length - 1}
            label="Next"
            onPress={next}
            orientation={orientation}
            placement="end"
          />
          {dragPreview}
        </div>
      </AnimateMutationProvider>
    </DragAndDropProvider>
  );
}
